/*
    Tenable.tsx -- Modal for configuring the Tenable integration.
*/
import Box from '@mui/material/Box';
import { type JSX, useState } from 'react';

import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE, ICON_DELETE_REMOVE } from 'Config/Icons';
import { ValidationError } from 'Models/ErrorTypes';
import { IntegrationName } from 'Models/ExternalIntegrations';

import styles from './ConfigureIntegration.module.css';
import { ConfigureIntegrationProps } from '../ExternalIntegrations';

export const Tenable = (props: ConfigureIntegrationProps): JSX.Element => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
    const [failureMessage, setFailureMessage] = useState<string | undefined>(undefined);
    const [accessKey, setAccessKey] = useState<string | undefined>(undefined);
    const [secretKey, setSecretKey] = useState<string | undefined>(undefined);

    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        const { name, value } = event.currentTarget;
        if (name === 'accessKey') setAccessKey(value);
        if (name === 'secretKey') setSecretKey(value);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        setIsSubmitting(true);
        setSuccessMessage(undefined);
        setFailureMessage(undefined);

        try {
            validateForm(accessKey, secretKey);
            await props.externalIntegrationsApi.setExternalIntegration(IntegrationName.TENABLE, {
                access_key: accessKey!,
                secret_key: secretKey!,
            });
            setSuccessMessage('Tenable integration configured.');
            props.getExternalIntegrations();
        } catch (err) {
            handleRequestError(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const validateForm = (accessKey?: string, secretKey?: string): void => {
        if (!accessKey || accessKey.length === 0) {
            throw new ValidationError('Invalid access key.');
        }
        if (!secretKey || secretKey.length === 0) {
            throw new ValidationError('Invalid secret key.');
        }
    };

    const handleDelete = async (): Promise<void> => {
        const confirmAlert = window.confirm('Are you sure you want to delete this integration? \r\n\r\n All limits created for this integration will also be deleted.');

        if (confirmAlert === false) {
            return;
        }

        setIsDeleting(true);
        setSuccessMessage(undefined);
        setFailureMessage(undefined);
        try {
            await props.externalIntegrationsApi.deleteExternalIntegration(IntegrationName.TENABLE);
            setSuccessMessage('Tenable integration deleted.');
            setIsDeleted(true);
            props.getExternalIntegrations();
        } catch (err) {
            handleRequestError(err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleRequestError = (err: Error): void => {
        setFailureMessage(err.message);
    };

    return (
        <Modal open header="Configure Tenable Integration">
            {successMessage && <Alert type="success">{successMessage}</Alert>}
            {failureMessage && <Alert type="error">{failureMessage}</Alert>}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <div className={styles.formFieldContainer}>
                    <FormFieldText formFieldId="accessKey" formFieldLabel="Access Key" required tooltip="The API access key provided by Tenable." handleChange={handleChange} />
                </div>
                <div className={styles.formFieldContainer}>
                    <FormFieldText formFieldId="secretKey" formFieldLabel="Secret Key" formFieldType="password" required tooltip="The API secret key provided by Tenable." handleChange={handleChange} />
                </div>
                <div className={styles.buttonRowContainer}>
                    <Button variant="danger" disabled={isDeleted || isDeleting} onClick={handleDelete} loadingText={'Deleting...'} isLoading={isDeleting} fontAwesomeImage={ICON_DELETE_REMOVE}>
                        Delete
                    </Button>
                    <div className={styles.buttonsRightContainer}>
                        <div className={styles.buttonPadding}>
                            <Button variant="secondary" onClick={props.hideModal} disabled={isSubmitting || isDeleting} fontAwesomeImage={ICON_CLOSE}>
                                Close
                            </Button>
                        </div>
                        <Button variant="submit" disabled={isDeleted || isDeleting} isLoading={isSubmitting} loadingText="Saving...">
                            Save
                        </Button>
                    </div>
                </div>
            </Box>
        </Modal>
    );
};
